import React, {
  useCallback, useContext, useMemo, useState
} from 'react';
import { useNavigate } from 'react-router-dom';

import {
  ButtonBar, ErrorMessage,
  RadioCardOptionGroup,
  ScreenHeader
} from '../../components';
import { useCreateOrUpdate, useFormData } from '../../hooks';

import { NewFAContext } from '.';

const formConfig = {
  fields: [
    { 
      id: 'ceremony-preference', 
      fieldName: 'ceremony_preference',
      type: 'text' as const,
      required: true 
    }
  ]
};


const options = [
  {
    id: 'yes',
    label: 'Yes, I want a ceremony',
    description: 'I want a service to celebrate my life.',
    name: 'ceremony',
    icon: 'celebration',
    value: 'yes'
  },
  {
    id: 'no',
    label: 'No, I do not want a ceremony',
    description: 'I do not want a service to celebrate my life.',
    name: 'ceremony',
    icon: 'cancel',
    value: 'no'
  },
  {
    id: 'unsure',
    label: 'Unsure',
    description: 'I am not sure about having a ceremony. Let my family decide.',
    name: 'ceremony',
    icon: 'help_outline',
    value: 'unsure'
  }
];

export function NewFACeremonyPreferences() {
  const navigate = useNavigate();
  const { funeralArrangement, update } = useContext(NewFAContext);
  const { handleCreateOrUpdate } = useCreateOrUpdate('funeral_arrangements');
  const { handleData, errors } = useFormData(formConfig);

  const [ error, setError ] = useState<string | null>(null);
    
  const handleBack = useCallback(() => {
    navigate(-1);
  }, [ navigate ]);

  const handleNext = useCallback(async () => {
    const selectedElement = document.querySelector('input[name="ceremony"]:checked');

    if (!selectedElement) {
      setError('Please select a ceremony preference.');
      return;
    }

    const selectedOption = (selectedElement as HTMLInputElement).value;
    
    // Save the ceremony preference
    const payload = {
      ceremony_preference: selectedOption,
      user_id: funeralArrangement?.user_id || null,
      person_id: funeralArrangement?.person_id || null
    };

    try {
      await handleCreateOrUpdate(funeralArrangement?.id || '', payload);
      await update();
    } catch (err) {
      console.error('Error saving ceremony preference:', err);
      setError('Failed to save preference. Please try again.');
      return;
    }

    // Navigate based on selection
    if (selectedOption === 'yes') {
      navigate('../ceremony-details');
    } else {
      navigate('../obituary-preferences');
    }
  }, [ navigate, funeralArrangement, handleCreateOrUpdate, update ]);

  const memoizedOptions = useMemo(() => options, []);

  return (
    <div className="new-fa-disposition screen">
      <ScreenHeader 
        title="Ceremony Preferences"
        description="Specify your ceremony preferences and arrangements."
        steps={6}
        step={1}
        handleBack={handleBack}
      />
      
      <RadioCardOptionGroup options={memoizedOptions} />
      <ErrorMessage message={error || errors[0]} />
      
      <ButtonBar
        handlePrimary={handleNext}
      />
    </div>
  );
}